import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Image, ModalGalleryService } from '@ks89/angular-modal-gallery';
import {
  RdsCarouselComponent,
  RdsCarouselItemDirective,
  RdsIconButtonComponent,
  RdsIconComponent,
  RdsLinkComponent,
} from '@rds/angular-components';
import { MarkdownComponent } from 'ngx-markdown';

import {
  RoomDetailsModel,
  RoomLayoutsModel,
  RoomServicesModel,
  RoomSettingsDurationRangeModel,
  ViewportService,
  durationToMinutes,
} from '@bookly/shared';

@Component({
  selector: 'bus-room-details-card',
  imports: [
    NgOptimizedImage,
    FormsModule,
    RdsIconComponent,
    ReactiveFormsModule,
    RdsLinkComponent,
    MarkdownComponent,
    RdsCarouselComponent,
    RdsCarouselItemDirective,
    RdsIconButtonComponent,
  ],
  templateUrl: './room-details-card.component.html',
  styleUrl: './room-details-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomDetailsCardComponent {
  readonly #modalGallery = inject(ModalGalleryService);
  protected readonly isMobileScreen = inject(ViewportService).isMobile;
  protected roomDetailsModel!: RoomDetailsModel;
  protected duration?: {
    minDuration?: string;
    maxDuration?: string;
  };
  protected availableServices: RoomServicesModel[] = [];
  protected readonly reportRoomUrl =
    'https://roche.service-now.com/siteservices?id=wsd_sc_cat_item_guide&sys_id=577dcb9e87c64718fc2363980cbb356c&table=sc_cat_item&searchTerm=event%20technology';

  @Input() public set roomDetails(details: RoomDetailsModel) {
    this.roomDetailsModel = details;
    const durationRange =
      this.roomDetailsModel?.manageSpaceConfiguration?.durationRange;
    const services = this.roomDetailsModel?.manageSpaceConfiguration?.services;
    if (durationRange) {
      this.duration = {
        minDuration:
          durationToMinutes(durationRange.min) > 0
            ? this.durationToString(durationRange.min)
            : undefined,
        maxDuration:
          durationToMinutes(durationRange.max) > 0
            ? this.durationToString(durationRange.max)
            : undefined,
      };
    }
    if (services) {
      this.availableServices = services;
    }
  }
  @Input() public set roomLayouts(value: RoomLayoutsModel[] | null) {
    if (value) {
      this.layouts = value;
      const defaultLayout = value.find(
        layout =>
          layout.layoutDexUUID ===
          this.roomDetailsModel.manageSpaceConfiguration?.defaultLayoutId
      );
      if (defaultLayout) {
        this.layoutControl.patchValue(defaultLayout);
      }
    }
  }
  layouts: RoomLayoutsModel[] = [];
  layoutControl = new FormControl<RoomLayoutsModel | null>(null);

  protected showGallery() {
    const images: Image[] = this.roomDetailsModel.photos.map(
      photo =>
        new Image(photo.id, { img: photo.url }, { img: photo.url }, 'eager')
    );
    this.#modalGallery.open({
      id: 1,
      images,
      currentImage: images[0],
    });
  }

  private durationToString(duration: RoomSettingsDurationRangeModel): string {
    return `${duration.days > 0 ? `${duration.days}d` : ''} ${duration.hours > 0 ? `${duration.hours}h` : ''} ${duration.minutes > 0 ? `${duration.minutes}m` : ''}`;
  }
}
